import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Toast from 'react-native-root-toast';

import { UPDATE_TICKET_PHOTOS } from '../../../common/graphql/ticket';
import { useOfflineMutation } from '../../offline';

const uniqueValues = values => Array.from(new Set((values || []).filter(Boolean)));
const isRemoteUrl = url => typeof url === 'string' && (url.startsWith('http://') || url.startsWith('https://'));

/**
 * Ticket photos for one ticket card: saves each captured batch (queued offline
 * outside training) one after another, and lists the saved, queued, saving and
 * just-captured photos together so the card shows them straight away.
 */
const useTicketPhotoUploads = ({
	ticket,
	captureMedia,
	capturedMediaBatch,
	processingMedia,
	isTraining,
	trainingOffline,
	scopedQueue,
}) => {
	const [savingPhotoUrls, setSavingPhotoUrls] = useState([]);
	const lastCapturedMediaBatchIdRef = useRef(null);
	const photoSaveChainRef = useRef(Promise.resolve());
	const isMountedRef = useRef(true);

	const [updateTicketPhotos] = useOfflineMutation(UPDATE_TICKET_PHOTOS, {
		configKey: 'updateTicketPhotos',
		disableOffline: isTraining,
	});

	const pendingPhotoQueueEntries = useMemo(() => {
		return (scopedQueue || []).filter(entry => entry.configKey === 'updateTicketPhotos' && String(entry.meta?.ticketId) === String(ticket.id));
	}, [scopedQueue, ticket.id]);
	const captureTicketMedia = useCallback(() => {
		if (trainingOffline) {
			Toast.show('Reconnect to continue training');
			return;
		}
		captureMedia();
	}, [captureMedia, trainingOffline]);

	const pendingPhotoUrls = useMemo(() => {
		const urls = new Set();

		pendingPhotoQueueEntries.forEach(entry => {
			(entry.variables?.imageUrls || []).forEach(url => urls.add(url));

			if (entry.meta?.localImageUrls?.length) {
				entry.meta.localImageUrls.forEach(url => urls.add(url));
			}
			if (entry.attachments?.length) {
				entry.attachments.forEach(attachment => {
					if (attachment.localUri) {
						urls.add(attachment.localUri);
					}
				});
			}
		});

		return Array.from(urls).filter(Boolean);
	}, [pendingPhotoQueueEntries]);

	const serverImageUrls = useMemo(() => (ticket.imageUrls || []).filter(isRemoteUrl), [ticket.imageUrls]);
	const unprocessedPhotoUrls = capturedMediaBatch && capturedMediaBatch.id !== lastCapturedMediaBatchIdRef.current
		? capturedMediaBatch.links || []
		: [];
	const imageUrls = uniqueValues([
		...serverImageUrls,
		...pendingPhotoUrls,
		...savingPhotoUrls,
		...unprocessedPhotoUrls,
	]);

	const hasPendingPhotoSync = pendingPhotoQueueEntries.length > 0;
	const hasPersistedImages = serverImageUrls.length > 0;
	const isCapturingOrSavingPhotos = processingMedia || savingPhotoUrls.length > 0 || unprocessedPhotoUrls.length > 0;
	const hasPhotoWorkPending = isCapturingOrSavingPhotos || hasPendingPhotoSync;

	useEffect(() => {
		if (!capturedMediaBatch || capturedMediaBatch.id === lastCapturedMediaBatchIdRef.current) {
			return;
		}

		lastCapturedMediaBatchIdRef.current = capturedMediaBatch.id;
		const batchImageUrls = uniqueValues(capturedMediaBatch.links);
		if (!batchImageUrls.length) {
			return;
		}

		setSavingPhotoUrls(existing => uniqueValues([...existing, ...batchImageUrls]));

		const savePhotoBatch = async () => {
			try {
				const result = await updateTicketPhotos({
					variables: {
						id: ticket.id,
						imageUrls: batchImageUrls,
					},
				});

				if (result?.offline) {
					Toast.show('Photo queued for upload');
				}
				else {
					Toast.show('Photo uploaded successfully');
				}
			}
			catch (e) {
				Toast.show('Error when uploading the photo');
			}
			finally {
				if (isMountedRef.current) {
					setSavingPhotoUrls(existing => existing.filter(url => !batchImageUrls.includes(url)));
				}
			}
		};

		photoSaveChainRef.current = photoSaveChainRef.current.then(savePhotoBatch);
	}, [capturedMediaBatch, updateTicketPhotos, ticket.id]);

	useEffect(() => {
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	return {
		imageUrls,
		hasPendingPhotoSync,
		hasPersistedImages,
		isCapturingOrSavingPhotos,
		hasPhotoWorkPending,
		captureTicketMedia,
	};
};

export default useTicketPhotoUploads;
